import { useState, useEffect, useMemo } from 'react';
import { X, AlertCircle, Loader, Calendar as CalendarIcon, Globe } from 'lucide-react';
import { calendarApiService } from '../services/calendarApi';
import { CalendarEventStatus, CalendarTag } from '../types/calendar';
import { motion, AnimatePresence } from 'framer-motion';
import MarkdownEditor from './MarkdownEditor';

interface CreateCalendarEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const STATUS_OPTIONS: CalendarEventStatus[] = ['active', 'cancelled', 'postponed'];

export const CreateCalendarEventModal = ({ isOpen, onClose, onSuccess }: CreateCalendarEventModalProps) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [status, setStatus] = useState<CalendarEventStatus>('active');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [tags, setTags] = useState<CalendarTag[]>([]);
  const [publish, setPublish] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Backend already filters tags by permissions, so all returned tags are accessible
  const visibleTags = tags;
  
  // Check if any selected tags are restricted - if so, publish cannot be enabled
  const hasRestrictedTags = useMemo(() => {
    return selectedTags.some(tagId => {
      const tag = tags.find(t => t.id === tagId);
      return tag?.visibility === 'restricted';
    });
  }, [selectedTags, tags]);

  useEffect(() => {
    if (isOpen) {
      fetchTags();
    }
  }, [isOpen]);

  const fetchTags = async () => {
    try {
      const tagsData = await calendarApiService.getAllTags();
      setTags(tagsData);
    } catch (err) {
      console.error('Failed to fetch tags:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // datetime-local input returns "YYYY-MM-DDTHH:mm" format (local time, no timezone)
      // new Date() interprets this as local time, getTime() converts to UTC milliseconds (Unix timestamp)
      const startTimestamp = new Date(startTime).getTime();
      const endTimestamp = endTime ? new Date(endTime).getTime() : null;
      
      if (isNaN(startTimestamp)) {
        throw new Error('Invalid start time');
      }
      if (endTimestamp !== null && isNaN(endTimestamp)) {
        throw new Error('Invalid end time');
      }

      await calendarApiService.createEvent({
        title,
        description,
        start_time: startTimestamp,
        end_time: endTimestamp,
        status,
        publish: hasRestrictedTags ? false : publish,
        tag_ids: selectedTags,
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setStartTime('');
    setEndTime('');
    setStatus('active');
    setSelectedTags([]);
    setPublish(false);
    setError(null);
    onClose();
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tagId)
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId];
      
      // If a restricted tag is added, disable publish
      const addedTag = tags.find(t => t.id === tagId);
      if (addedTag?.visibility === 'restricted' && !prev.includes(tagId)) {
        setPublish(false);
      }
      
      return newTags;
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay z-50"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="modal-panel max-w-3xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-6 h-6 text-accent-400" />
              <h2 className="text-lg font-semibold tracking-tight text-white">Create Calendar Event</h2>
            </div>
            <button onClick={handleClose} className="glass-button p-2 rounded-full">
              <X className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <div className="glass-card p-4 mb-4 border-l-4 border-red-400 bg-red-500/10">
              <div className="flex items-center space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                <span className="text-red-300">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="glass-input w-full"
                placeholder="Event title..."
                required
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Description <span className="text-red-400">*</span>
              </label>
              <MarkdownEditor
                value={description}
                onChange={setDescription}
                height={300}
                placeholder="Enter event description in markdown..."
              />
              <p className="text-xs text-glass-400 mt-1">
                Markdown editor with live preview - use code blocks, tables, lists, etc.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  Start Time <span className="text-red-400">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="glass-input w-full"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-glass-400 mb-1.5">
                  End Time (optional)
                </label>
                <input
                  type="datetime-local"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="glass-input w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as CalendarEventStatus)}
                className="glass-input w-full"
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-glass-400 mb-1.5">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {visibleTags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      selectedTags.includes(tag.id)
                        ? 'text-white'
                        : 'text-glass-400'
                    }`}
                    style={{
                      backgroundColor: selectedTags.includes(tag.id) ? tag.color : 'rgba(255, 255, 255, 0.1)',
                      border: `1px solid ${tag.color}`,
                    }}
                  >
                    {tag.name}
                    {tag.visibility === 'restricted' && (
                      <span className="ml-1 text-xs opacity-75">🔒</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 glass-card border-l-4 border-accent-400/50">
              <input
                type="checkbox"
                id="publish"
                checked={publish}
                onChange={(e) => setPublish(e.target.checked)}
                disabled={hasRestrictedTags}
                className="mt-1 w-4 h-4 rounded border-glass-300 bg-dark-800 text-accent-400 focus:ring-accent-400 focus:ring-2"
              />
              <div className="flex-1">
                <label htmlFor="publish" className="flex items-center space-x-2 cursor-pointer">
                  <Globe className="w-4 h-4 text-accent-400" />
                  <span className="text-sm font-medium text-white">Publish to Public Calendar</span>
                </label>
                <p className="text-xs text-glass-400 mt-1">
                  {hasRestrictedTags 
                    ? 'Cannot publish events with restricted tags. Remove restricted tags to enable publishing.'
                    : 'Make this event visible on the public community calendar for all members to see.'
                  }
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="glass-button-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="glass-button"
                disabled={isSubmitting || !title || !description || !startTime}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Event'
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

